import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);

  const load = async () => {
    try {
      const [c, n] = await Promise.all([
        api.get("/notifications/unread-count"),
        api.get("/notifications/my"),
      ]);
      setCount(c.data.count);
      setItems(n.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.put("/notifications/read-all");
    load();
  };

  const markOne = async (id) => {
    await api.put(`/notifications/${id}/read`);
    load();
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-2xl bg-white border-2 border-slate-200 hover:border-slate-300 flex items-center justify-center"
        aria-label="Notifications"
        data-testid="notifications-bell"
      >
        <Bell size={18} strokeWidth={2.5} className="text-slate-700" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[320px] sm:w-[360px] card-clay z-40 overflow-hidden" data-testid="notif-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-100">
              <div className="font-fredoka font-bold">Notifications</div>
              {count > 0 && (
                <button onClick={markAll} className="text-xs text-green-700 font-bold inline-flex items-center gap-1" data-testid="notif-mark-all">
                  <CheckCheck size={14}/> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-auto">
              {items.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">No notifications yet</div>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => markOne(n.id)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${!n.read ? "bg-amber-50/40" : ""}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm text-slate-800">{n.title}</div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0"/>}
                    </div>
                    {n.body && <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>}
                    <div className="text-[10px] text-slate-400 mt-1 uppercase font-bold tracking-wider">{new Date(n.created_at).toLocaleString()}</div>
                  </button>
                ))
              )}
            </div>
            <div className="border-t-2 border-slate-100 p-2 text-center">
              <Link to="/notifications" onClick={() => setOpen(false)} className="text-sm font-bold text-green-700">View all →</Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
